import { motion } from "framer-motion";
import Chapter from "./Chapter";

const TIERS = [
  {
    id: "iso27001",
    badge: "Free",
    name: "ISO 27001",
    price: "₹0",
    tag: "forever",
    body: "The full crosswalk for ISO 27001 — controls, evidence, risk, policies, audit trail. No credit card, no time-lock, no watered-down features.",
    cta: "Start free",
    cta_test: "pricing-cta-free",
    highlight: true,
    features: ["93 controls mapped", "Full evidence engine", "Hash-chain audit log", "1 admin · 5 collaborators"],
  },
  {
    id: "scale",
    badge: "Scale",
    name: "Multi-framework",
    price: "Let's talk",
    anchor: "custom · every stack is different",
    tag: "onboarding design partners",
    body: "Add any framework you need — SOC 2, GDPR, DPDP, HIPAA, PCI, ISO 42001. Your existing evidence keeps counting across all of them. Founder pricing while we onboard design partners.",
    cta: "Talk to us",
    cta_test: "pricing-cta-scale",
    highlight: false,
    features: ["All 20 frameworks available", "DPDP-native", "ISO 42001 AI governance", "Unlimited collaborators"],
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="relative py-14 md:py-20 hairline-t">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Chapter number="09" label="On-ramp · pricing" />

        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="font-display text-[36px] md:text-[54px] leading-[1.04] tracking-[-0.022em] text-balance mb-12 max-w-[26ch]"
        >
          Start with <span className="grad-text">ISO 27001 free</span>. Add frameworks as you grow.
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22,1,0.36,1] }}
              className={`machined p-8 md:p-10 relative ${t.highlight ? "grad-border" : ""}`}
              style={t.highlight ? { borderRadius: 14 } : {}}
            >
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-sm border font-mono text-[9.5px] tracking-[0.22em] uppercase ${t.highlight ? "border-brand-red/50 text-brand-red" : "border-white/15 text-white/60"}`}>
                  {t.badge}
                </span>
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">{t.tag}</span>
              </div>

              <div className="mt-8 flex items-baseline gap-3">
                <div className="font-display text-[44px] md:text-[52px] leading-none tracking-[-0.02em]">{t.price}</div>
                <div className="text-ink-muted text-[13px]">{t.name}</div>
              </div>
              {t.anchor && (
                <div className="mt-2 font-mono text-[11px] tracking-[0.06em] text-white/55">{t.anchor}</div>
              )}

              <p className="mt-5 text-ink-muted text-[14.5px] leading-relaxed max-w-[46ch]">{t.body}</p>

              <ul className="mt-6 space-y-2.5">
                {t.features.map((f) => (
                  <li key={f} className="flex items-center gap-3 text-[13.5px]">
                    <span className={`w-1 h-1 rounded-full ${t.highlight ? "bg-brand-red" : "bg-white/30"}`} />
                    <span className="text-white/85">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#demo"
                data-testid={t.cta_test}
                className={`mt-8 inline-flex ${t.highlight ? "cta" : "cta-secondary"}`}
              >
                {t.cta}
                <span aria-hidden>→</span>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
